import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api';
import PollCard from './PollCard';
import CreatePollModal from './CreatePollModal';
import EditPollModal from './EditPollModal';
import OnboardingTour from './OnboardingTour';
import AppShell from './AppShell';
import { buildDashboardTourSteps, isTourDone, markTourDone } from './onboardingSteps';
import './Dashboard.css';

const ACTIVITY_REFRESH_MS = 30000;
const ACTIVITY_KEYS = ['response_count', 'answer_count', 'last_response_at'];
const activityOf = (row) => Object.fromEntries(ACTIVITY_KEYS.map((k) => [k, row[k]]));
const mergeActivity = (old, updated) => {
    const merged = { ...old, ...updated };
    ACTIVITY_KEYS.forEach((k) => {
        if (updated[k] === null || updated[k] === undefined) merged[k] = old[k];
    });
    return merged;
};

const Dashboard = () => {
    const [user, setUser] = useState(null);
    const [polls, setPolls] = useState([]);
    const [pollFilter, setPollFilter] = useState('all');
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [editingPoll, setEditingPoll] = useState(null);
    const [recentlyCreatedInactivePollId, setRecentlyCreatedInactivePollId] = useState(null);
    const [telegramWebAuthEnabled, setTelegramWebAuthEnabled] = useState(true);
    const [tourOpen, setTourOpen] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const autoTourCheckedRef = useRef(false);

    useEffect(() => {
        api.get('/auth/config')
            .then((res) => setTelegramWebAuthEnabled(res.data.telegram_web_auth_enabled !== false))
            .catch(() => setTelegramWebAuthEnabled(false));

        Promise.all([
            api.get('/users/me'),
            api.get('/polls/me')
        ])
            .then(([userRes, pollsRes]) => {
                setUser(userRes.data);
                setPolls(pollsRes.data);
                const storedHintPollId = localStorage.getItem('activation_hint_poll_id');
                if (storedHintPollId) {
                    const hintPollId = Number(storedHintPollId);
                    const hintPollExists = pollsRes.data.some((poll) => poll.id === hintPollId && !poll.is_active);
                    if (hintPollExists) {
                        setRecentlyCreatedInactivePollId(hintPollId);
                    } else {
                        localStorage.removeItem('activation_hint_poll_id');
                    }
                }
                setLoading(false);
            })
            .catch(err => {
                console.error("Failed to fetch data", err);
                setLoading(false);
            });
    }, []);

    // Пока кабинет открыт, респонденты и ответы активных опросов обновляются сами (раз в 30 секунд)
    const hasActivePolls = polls.some((p) => p.is_active && !p.archived_at);
    useEffect(() => {
        if (!hasActivePolls) return undefined;
        const refresh = () => {
            if (document.visibilityState === 'hidden') return;
            api.get('/polls/me/activity')
                .then((res) => {
                    const byId = new Map(res.data.map((row) => [row.poll_id, row]));
                    setPolls((prev) => prev.map((p) => (byId.has(p.id) ? { ...p, ...activityOf(byId.get(p.id)) } : p)));
                })
                .catch(() => {});
        };
        const timer = setInterval(refresh, ACTIVITY_REFRESH_MS);
        return () => clearInterval(timer);
    }, [hasActivePolls]);

    // Обучение: автоматически при первом входе, по ссылке ?tour=1 (из профиля) или пунктом меню.
    // Остальные параметры приходят из меню на других страницах: ?create=1, ?filter=inactive,
    // ?edit=<id> — открыть настройки опроса (ссылка из «Ваших презентаций»).
    useEffect(() => {
        if (!user) return;
        const tour = searchParams.get('tour') === '1';
        const create = searchParams.get('create') === '1';
        const filter = searchParams.get('filter');
        const edit = parseInt(searchParams.get('edit'), 10);
        if (tour || create || filter || edit) setSearchParams({}, { replace: true });
        if (edit > 0) setEditingPoll({ id: edit });
        if (filter === 'inactive' || filter === 'active' || filter === 'archive') setPollFilter(filter);
        if (create && user.status === 'active') setShowCreateModal(true);
        // Автоматически обучение показываем один раз за визит и не поверх окна, открытого из меню
        const autoTour = !autoTourCheckedRef.current && !create && !edit && !isTourDone(user.id);
        autoTourCheckedRef.current = true;
        if (tour || autoTour) setTourOpen(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, searchParams]);

    const closeTour = () => {
        setTourOpen(false);
        if (user) markTourDone(user.id);
    };

    const handlePollUpdate = (updatedPoll) => {
        // Счётчики активности приходят только в списке опросов: если в ответе их нет, оставляем прежние
        setPolls((prev) => prev.map((p) => (p.id === updatedPoll.id ? mergeActivity(p, updatedPoll) : p)));
        if (updatedPoll.is_active && updatedPoll.id === recentlyCreatedInactivePollId) {
            setRecentlyCreatedInactivePollId(null);
            localStorage.removeItem('activation_hint_poll_id');
        }
    };

    const handlePollDelete = (pollId) => {
        setPolls(polls.filter(p => p.id !== pollId));
    };

    const handlePollCreated = (newPoll) => {
        setPolls([{ ...newPoll, response_count: 0, answer_count: 0, last_response_at: null }, ...polls]);
        setRecentlyCreatedInactivePollId(newPoll.id);
        localStorage.setItem('activation_hint_poll_id', String(newPoll.id));
        alert('Опрос создан. Не забудьте активировать его перед отправкой ссылки респондентам.');
        setEditingPoll(newPoll); // Auto-open editor for new poll
    };

    // Архивные опросы живут на своей вкладке и не мешают в рабочем списке
    const workingPolls = polls.filter((poll) => !poll.archived_at);
    const archivedPolls = polls
        .filter((poll) => poll.archived_at)
        .sort((a, b) => new Date(b.archived_at) - new Date(a.archived_at));
    const inactivePollsCount = workingPolls.filter((poll) => !poll.is_active).length;
    const activePollsCount = workingPolls.length - inactivePollsCount;
    const filteredPolls = pollFilter === 'archive' ? archivedPolls : workingPolls.filter((poll) => (
        pollFilter === 'inactive' ? !poll.is_active : pollFilter === 'active' ? poll.is_active : true
    ));
    const showPolls = (filter) => {
        setPollFilter(filter);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };
    

    if (loading || !user) return <div className="dashboard-loading">Загрузка...</div>;


    return (
        <AppShell
            user={user}
            counts={{ total: workingPolls.length, active: activePollsCount, archived: archivedPolls.length }}
            section="polls"
            title="Мои опросы"
            onCreatePoll={() => setShowCreateModal(true)}
            onShowPolls={showPolls}
            onStartTour={() => setTourOpen(true)}
        >
        <div className="dashboard">
            <main className="dashboard-main">
                {!telegramWebAuthEnabled && !user.email && (
                    <div className="dashboard-warning" style={{
                        marginBottom: 'var(--space-4)',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        background: '#fff7ed',
                        border: '1px solid #fdba74',
                        color: '#9a3412',
                    }}>
                        <strong>Функционал организатора в Telegram отключён.</strong>{' '}
                        Используйте веб-версию: зарегистрируйтесь или войдите по email на странице входа.
                        {' '}
                        <button
                            type="button"
                            className="login-link"
                            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                            onClick={() => {
                                localStorage.removeItem('token');
                                navigate('/login');
                            }}
                        >
                            Перейти к регистрации
                        </button>
                    </div>
                )}
                <div className="dashboard-toolbar">
                    <h3>Мои опросы</h3>
                    {user.status === 'active' && (
                        <button
                            type="button"
                            className="dashboard-action-btn dashboard-action-btn--create dashboard-create-mobile"
                            onClick={() => setShowCreateModal(true)}
                            data-tour="create-poll"
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
                                <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                            </svg>
                            Создать опрос
                        </button>
                    )}
                </div>
                {(user.status === 'pending' || user.status === 'blocked') ? (
                    <div className="dashboard-notice">
                        <h2>
                            {user.status === 'pending' ? 'Доступ проверяется' : 'Доступ закрыт'}
                        </h2>
                        <p>
                            {user.status === 'pending'
                                ? 'Ваша учетная запись ожидает подтверждения администратором. Вы получите уведомление в боте, когда доступ будет открыт.'
                                : 'Ваша учетная запись заблокирована или срок действия истек. Обратитесь к администратору.'}
                        </p>
                    </div>
                ) : (
                    <>
                        {polls.length === 0 ? (
                            <div className="dashboard-empty">
                                <p>
                                    У вас пока нет опросов.
                                </p>
                                <p className="hint">
                                    Нажмите «Создать опрос», чтобы начать.
                                </p>
                            </div>
                        ) : (
                            <div>
                                <div className="dashboard-filter-row">
                                    <button
                                        type="button"
                                        className={`dashboard-filter-btn ${pollFilter === 'all' ? 'dashboard-filter-btn--active' : ''}`}
                                        onClick={() => setPollFilter('all')}
                                    >
                                        Все ({workingPolls.length})
                                    </button>
                                    <button
                                        type="button"
                                        className={`dashboard-filter-btn ${pollFilter === 'active' ? 'dashboard-filter-btn--active' : ''}`}
                                        onClick={() => setPollFilter('active')}
                                    >
                                        Активные ({activePollsCount})
                                    </button>
                                    <button
                                        type="button"
                                        className={`dashboard-filter-btn ${pollFilter === 'inactive' ? 'dashboard-filter-btn--active' : ''}${inactivePollsCount > 0 ? ' dashboard-filter-btn--warn' : ''}`}
                                        onClick={() => setPollFilter('inactive')}
                                        title={inactivePollsCount > 0 ? 'Респонденты не смогут отвечать, пока опрос не активирован' : undefined}
                                    >
                                        Неактивные ({inactivePollsCount})
                                    </button>
                                    <button
                                        type="button"
                                        className={`dashboard-filter-btn dashboard-filter-btn--archive ${pollFilter === 'archive' ? 'dashboard-filter-btn--active' : ''}`}
                                        onClick={() => setPollFilter('archive')}
                                        title="Завершённые опросы: ответы не принимаются, результаты сохранены"
                                    >
                                        Архив ({archivedPolls.length})
                                    </button>
                                </div>
                                {pollFilter === 'archive' && archivedPolls.length > 0 && (
                                    <p className="dashboard-archive-note">
                                        {user?.subscription_tier === 'free'
                                            ? 'Архивные опросы не принимают ответы, но хранят результаты. На бесплатном тарифе они учитываются в лимите опросов.'
                                            : 'Архивные опросы не принимают ответы, хранят результаты и не занимают места в лимите тарифа.'}
                                    </p>
                                )}
                                {filteredPolls.length === 0 && pollFilter !== 'all' && (
                                    <div className="dashboard-empty dashboard-empty--compact">
                                        <p>{pollFilter === 'inactive' ? 'Неактивных опросов нет.'
                                            : pollFilter === 'archive' ? 'Архив пуст. Завершённый опрос можно отправить сюда через меню «Ещё» → «В архив»: результаты сохранятся, а опрос не будет мешать в списке.'
                                            : 'Активных опросов нет.'}</p>
                                    </div>
                                )}
                                {filteredPolls.length === 0 && pollFilter === 'all' && archivedPolls.length > 0 && (
                                    <div className="dashboard-empty dashboard-empty--compact">
                                        <p>Все опросы в архиве. Создайте новый или верните опрос из архива.</p>
                                    </div>
                                )}
                                {filteredPolls.map((poll, index) => (
                                    <PollCard
                                        key={poll.id}
                                        tourAnchor={index === 0}
                                        poll={poll}
                                        onUpdate={handlePollUpdate}
                                        onDelete={handlePollDelete}
                                        onEdit={() => setEditingPoll(poll)}
                                        userTier={user?.subscription_tier}
                                        userRole={user?.role}
                                        userEmail={user?.email}
                                        hasCorporateAccess={user?.has_corporate_access}
                                        showActivationHint={poll.id === recentlyCreatedInactivePollId && !poll.is_active}
                                    />
                                ))}
                            </div>
                        )}
                    </>
                )}
            </main>

            {tourOpen && (
                <OnboardingTour
                    steps={buildDashboardTourSteps({ tier: user.subscription_tier, hasPolls: filteredPolls.length > 0 })}
                    onClose={closeTour}
                />
            )}

            {showCreateModal && (
                <CreatePollModal
                    onClose={() => setShowCreateModal(false)}
                    onCreated={handlePollCreated}
                    userTier={user?.subscription_tier}
                />
            )}

            {editingPoll && (
                <React.Suspense fallback={<div>Loading editor...</div>}>
                    {/* Dynamic import or just regular import if already imported (it's not imported yet in Dashboard) */}
                    <EditPollModal
                        poll={editingPoll}
                        userTier={user?.subscription_tier}
                        onClose={() => setEditingPoll(null)}
                        onUpdate={() => {
                            // Refresh poll data logic could go here or inside modal
                            api.get(`/polls/${editingPoll.id}`)
                                .then(res => {
                                    handlePollUpdate(res.data);
                                    // Update local editingPoll state too if needed, but usually modal handles its own data fetching
                                    // But to be safe, let's update it
                                    setEditingPoll(res.data);
                                })
                                .catch(err => console.error('Failed to refresh poll', err));
                        }}
                    />
                </React.Suspense>
            )}
            
        </div>
        </AppShell>
    );
};

export default Dashboard;
